import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as FulfilledIcon,
  Cancel as CancelledIcon,
  Pending as PendingIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  FilterList as FilterIcon,
  LocalMall as OrderIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const ShopifyOrdersPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Sample data - replace with actual Shopify API call
  const sampleOrders = [
    {
      id: '#1001',
      orderNumber: 'ORD-2024-001',
      customerName: 'John Doe',
      customerEmail: 'john.doe@example.com',
      date: '2024-03-15T10:30:00',
      items: 3,
      total: 125.50,
      status: 'fulfilled',
      paymentStatus: 'paid',
      fulfillmentStatus: 'fulfilled',
      shippingAddress: 'Brussels, Belgium',
    },
    {
      id: '#1002',
      orderNumber: 'ORD-2024-002',
      customerName: 'Jane Smith',
      customerEmail: 'jane.smith@example.com',
      date: '2024-03-16T14:20:00',
      items: 2,
      total: 89.99,
      status: 'pending',
      paymentStatus: 'pending',
      fulfillmentStatus: 'unfulfilled',
      shippingAddress: 'Antwerp, Belgium',
    },
    {
      id: '#1003',
      orderNumber: 'ORD-2024-003',
      customerName: 'Bob Johnson',
      customerEmail: 'bob.j@example.com',
      date: '2024-03-17T09:15:00',
      items: 5,
      total: 299.99,
      status: 'processing',
      paymentStatus: 'paid',
      fulfillmentStatus: 'partial',
      shippingAddress: 'Ghent, Belgium',
    },
  ];

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // TODO: Replace with actual Shopify API call
      // const response = await getShopifyOrders();
      setTimeout(() => {
        setOrders(sampleOrders);
        setLoading(false);
      }, 1000);
    } catch (error) {
      toast.error('Failed to fetch orders');
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event, order) => {
    setMenuAnchor(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedOrder(null);
  };

  const handleViewDetails = () => {
    toast.info(`Viewing details for order ${selectedOrder?.orderNumber}`);
    handleMenuClose();
  };

  const handleFulfillOrder = () => {
    toast.success(`Order ${selectedOrder?.orderNumber} marked as fulfilled`);
    handleMenuClose();
  };

  const handleCancelOrder = () => {
    toast.warning(`Order ${selectedOrder?.orderNumber} cancelled`);
    handleMenuClose();
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch = searchTerm === '' ||
      order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerEmail.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      fulfilled: 'success',
      pending: 'warning',
      processing: 'info',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const getPaymentStatusColor = (status) => {
    return status === 'paid' ? 'success' : 'warning';
  };

  const formatCurrency = (amount) => {
    return `€${amount.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getOrderStats = () => {
    return {
      total: orders.length,
      fulfilled: orders.filter(o => o.status === 'fulfilled').length,
      pending: orders.filter(o => o.status === 'pending').length,
      revenue: orders.reduce((sum, o) => sum + o.total, 0),
    };
  };

  const stats = getOrderStats();

  if (loading && orders.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5 }}>
              {t('online_store_orders') || 'Online Store Orders'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('manage_shopify_orders') || 'Manage your Shopify orders and fulfillment'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('refresh') || 'Refresh'}>
              <IconButton onClick={fetchOrders} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              sx={{ borderRadius: 2 }}
            >
              {t('export') || 'Export'}
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      {t('total_orders') || 'Total Orders'}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#667eea', mt: 1 }}>
                      {stats.total}
                    </Typography>
                  </Box>
                  <OrderIcon sx={{ fontSize: 48, color: '#667eea', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      {t('fulfilled') || 'Fulfilled'}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981', mt: 1 }}>
                      {stats.fulfilled}
                    </Typography>
                  </Box>
                  <FulfilledIcon sx={{ fontSize: 48, color: '#10b981', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      {t('pending') || 'Pending'}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b', mt: 1 }}>
                      {stats.pending}
                    </Typography>
                  </Box>
                  <PendingIcon sx={{ fontSize: 48, color: '#f59e0b', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      {t('total_revenue') || 'Total Revenue'}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#8b5cf6', mt: 1 }}>
                      {formatCurrency(stats.revenue)}
                    </Typography>
                  </Box>
                  <OrderIcon sx={{ fontSize: 48, color: '#8b5cf6', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder={t('search_orders') || 'Search orders by order number, customer name or email...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>{t('status') || 'Status'}</InputLabel>
              <Select
                value={statusFilter}
                label={t('status') || 'Status'}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('all') || 'All'}</MenuItem>
                <MenuItem value="fulfilled">{t('fulfilled') || 'Fulfilled'}</MenuItem>
                <MenuItem value="pending">{t('pending') || 'Pending'}</MenuItem>
                <MenuItem value="processing">{t('processing') || 'Processing'}</MenuItem>
                <MenuItem value="cancelled">{t('cancelled') || 'Cancelled'}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>
      </Box>

      {/* Orders Table */}
      <Paper sx={{ borderRadius: 2, boxShadow: 2 }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#f9fafb' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>{t('order') || 'Order'}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>{t('date') || 'Date'}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>{t('customer') || 'Customer'}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#374151' }}>{t('items') || 'Items'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#374151' }}>{t('total') || 'Total'}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#374151' }}>{t('payment') || 'Payment'}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#374151' }}>{t('status') || 'Status'}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#374151' }}>{t('actions') || 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <OrderIcon sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {t('no_orders_found') || 'No orders found'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('try_different_search') || 'Try adjusting your search or filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((order) => (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{ '&:hover': { bgcolor: '#f9fafb', cursor: 'pointer' } }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#667eea' }}>
                            {order.orderNumber}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(order.date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {order.customerName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.customerEmail}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={order.items}
                          size="small"
                          sx={{ fontWeight: 600, minWidth: 40 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#10b981' }}>
                          {formatCurrency(order.total)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={order.paymentStatus}
                          color={getPaymentStatusColor(order.paymentStatus)}
                          size="small"
                          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={order.status}
                          color={getStatusColor(order.status)}
                          size="small"
                          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, order)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredOrders.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <OrderIcon sx={{ mr: 1, fontSize: 20 }} />
          {t('view_details') || 'View Details'}
        </MenuItem>
        <MenuItem onClick={handleFulfillOrder}>
          <ShippingIcon sx={{ mr: 1, fontSize: 20 }} />
          {t('fulfill_order') || 'Fulfill Order'}
        </MenuItem>
        <MenuItem onClick={handleCancelOrder} sx={{ color: 'error.main' }}>
          <CancelledIcon sx={{ mr: 1, fontSize: 20 }} />
          {t('cancel_order') || 'Cancel Order'}
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default ShopifyOrdersPage;
